import { ComponentClass, MouseEventHandler, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Select, {
  ActionMeta,
  components,
  MultiValueProps,
  Props as SelectProps,
  ValueType,
} from "react-select";
import {
  arrayMove,
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortableHandle,
  SortEndHandler,
} from "react-sortable-hoc";
import styled from "styled-components";
import { useLocaleProvider } from "../../components/locale-provider";
import { Tag } from "./domain/Tag";
import { TagAssociation } from "./domain/TagAssociation";
import { messages } from "./TagUpdateModal.messages";

interface Props {
  show: boolean;
  tags: Tag[];
  tagAssociation: TagAssociation;
  handleSave: (tagAssociation: TagAssociation) => void;
  handleClose: () => void;
}

interface SelectOption {
  value: string;
  label: string;
  tag: Tag;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem 0;
  gap: 1rem;

  & > div {
    width: 100%;
  }

  & > button {
    padding: 0;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #929292 !important;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
`;

export function TagUpdateModal({ show, tags, tagAssociation, handleSave, handleClose }: Props) {
  const [association, setAssociation] = useState<TagAssociation>(tagAssociation);
  const { currentLocale } = useLocaleProvider();
  const { formatMessage } = useIntl();

  const title = association.getTitle(currentLocale);
  const options = useMemo(() => tags.map(tag => tagToOption(tag, currentLocale)), [
    tags,
    currentLocale,
  ]);
  const tagsAssociated = useMemo(
    () => association.tags.map(tag => tagToOption(tag, currentLocale)),
    [association, currentLocale]
  );

  function onSelectChange(value: ValueType<SelectOption, true>, _: ActionMeta<SelectOption>) {
    const newTags: Tag[] = Array.isArray(value) ? value.map(option => option.tag) : [];
    const { id, ticker, translations, type } = association;

    setAssociation(TagAssociation.of(ticker, translations, type, newTags, id));
  }

  function onSave() {
    handleSave(association);
  }

  function onClearAll() {
    const { id, ticker, translations, type } = association;
    setAssociation(TagAssociation.of(ticker, translations, type, [], id));
  }

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    const newTags = arrayMove(tagsAssociated, oldIndex, newIndex).map(option => option.tag);

    const { id, ticker, translations, type } = association;
    setAssociation(TagAssociation.of(ticker, translations, type, newTags, id));
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <SortableSelect
            useDragHandle
            axis="xy"
            onSortEnd={onSortEnd}
            distance={4}
            isMulti
            options={options}
            value={tagsAssociated}
            onChange={onSelectChange}
            components={{
              MultiValue: SortableMultiValue,
              MultiValueLabel: SortableMultiValueLabel,
            }}
            closeMenuOnSelect={false}
            isClearable={false}
            placeholder={formatMessage(messages.selectPlaceholder)}
          />
          <Button variant="link" onClick={onClearAll}>
            <FormattedMessage {...messages.btnClearAll} />
          </Button>
        </ModalContent>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsContainer>
          <Button variant="primary" onClick={onSave}>
            <FormattedMessage {...messages.btnSave} />
          </Button>
          <SecondaryButton variant="secondary" onClick={handleClose}>
            <FormattedMessage {...messages.btnReturn} />
          </SecondaryButton>
        </ButtonsContainer>
      </Modal.Footer>
    </Modal>
  );
}

function tagToOption(tag: Tag, locale: string): SelectOption {
  return {
    value: tag.ticker,
    label: tag.getTitle(locale),
    tag: tag,
  };
}

// These components should be encapsulated into a generic component, but it's a nightmare because types
const SortableMultiValue = (SortableElement((props: MultiValueProps<SelectOption>) => {
  // this prevents the menu from being opened/closed when the user clicks
  // on a value to begin dragging it. ideally, detecting a click (instead of
  // a drag) would still focus the control and toggle the menu, but that
  // requires some magic with refs that are out of scope for this example
  const onMouseDown: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  return (
    <div style={{ zIndex: 9999 }}>
      <components.MultiValue {...props} innerProps={innerProps} />
    </div>
  );
}) as unknown) as ComponentClass<MultiValueProps<SelectOption>>;

const SortableMultiValueLabel = SortableHandle((props: any) => (
  <components.MultiValueLabel {...props} />
));

const SortableSelect = SortableContainer(Select) as ComponentClass<
  SelectProps<SelectOption, true> & SortableContainerProps
>;
